"use client";

import { Children, createContext, useContext, useLayoutEffect, useRef, type ReactNode } from "react";

export const HeaderControlsContext = createContext<{ navigation: ReactNode; actions: ReactNode } | null>(null);

/** Page identity and actions share one header with the shell's global controls. */
export default function PageHeader({ children, loading = false }: { children: ReactNode; loading?: boolean }) {
  const headerRef = useRef<HTMLElement>(null);
  const controls = useContext(HeaderControlsContext);
  const [identity, ...actions] = Children.toArray(children);

  useLayoutEffect(() => {
    if (loading) return;
    const header = headerRef.current;
    const shell = header?.closest<HTMLElement>("[data-app-shell]") ?? header?.closest("main");
    if (!header || !shell) return;
    // Share the measured height with both side panels. The shell survives
    // route changes, preserving header alignment through loading states.
    const rememberHeight = () => {
      const height = header.getBoundingClientRect().height;
      if (height > 0) shell.style.setProperty("--page-header-height", `${height}px`);
    };
    rememberHeight();
    const observer = new ResizeObserver(rememberHeight);
    observer.observe(header);
    return () => observer.disconnect();
  }, [loading]);

  return (
    <header ref={headerRef} data-page-header data-page-header-loading={loading || undefined} style={loading ? { minHeight: "var(--page-header-height, 4.5rem)" } : undefined} className="@container/header shrink-0 border-b border-zinc-800 px-3 py-3 sm:px-6 sm:py-3.5">
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
        {controls?.navigation}
        <div className="min-w-0 flex-1 basis-20 [&_p]:truncate">{identity}</div>
        {actions.length > 0 && (
          <div className="order-3 flex w-full min-w-0 flex-wrap items-center gap-2 @[40rem]/header:order-none @[40rem]/header:w-auto">
            {actions}
          </div>
        )}
        {controls?.actions}
      </div>
    </header>
  );
}
